<script lang="ts">
	import chevron from "@core/assets/icons/chevron-arrow.svg?raw";
	import type { Category } from "@core/schema/Category.js";
	import { contactId } from "@core/schema/ids/contactId.js";
	import { getAboutPath } from "@core/schema/paths/getAboutPath.js";
	import { getCataloguePath } from "@core/schema/paths/getCataloguePath.js";
	import heroPhotoMobile from "../assets/final_photos/DSC_0166.jpg?webp&w=768&quality=65&imagetools";
	import heroPhoto from "../assets/final_photos/DSC_0166.jpg?webp&w=1440&quality=65&imagetools";
	import service2Image from "../assets/services/elektromontazni.jpg?webp&w=96&h=96&imagetools";
	import service6Image from "../assets/services/filtrace.jpg?webp&w=96&h=96&imagetools";
	import service1Image from "../assets/services/montaze.jpg?webp&w=96&h=96&imagetools";
	import service8Image from "../assets/services/nahradni-dily.jpg?webp&w=96&h=96&imagetools";
	import service7Image from "../assets/services/revize.jpg?webp&w=96&h=96&imagetools";
	import service5Image from "../assets/services/servis.jpg?webp&w=96&h=96&imagetools";
	import service4Image from "../assets/services/svarecske.jpg?webp&w=96&h=96&imagetools";
	import service3Image from "../assets/services/zamecnicke.jpg?webp&w=96&h=96&imagetools";
	import PageHero from "../components/PageHero.svelte";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import ContactForm from "./ContactForm.svelte";
	import ProcessWorkflow from "./ProcessWorkflow.svelte";
	import translations from "./ProductionAndServicesPage.translations.json" with { type: "json" };
	import ServiceCardsGrid from "./ServiceCardsGrid.svelte";
	import WhyCooperateCarousel from "./WhyCooperateCarousel.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";
	import Link from "./common/Link.svelte";
	import Media from "./common/Media.svelte";
	import StickySection from "./common/StickySection.svelte";
	import TextWithIcon from "./common/TextWithIcon.svelte";
	import { ButtonVariant } from "./common/getButtonClass.js";

	const { categories }: { categories: Category[] } = $props();
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	const serviceImages = [
		service1Image,
		service2Image,
		service3Image,
		service4Image,
		service5Image,
		service6Image,
		service7Image,
		service8Image,
	];

	let expandedCards = $state<Set<number>>(new Set());

	function toggleCard(index: number): void {
		if (typeof window !== "undefined" && window.innerWidth >= 1536) {
			return; // Na velkých obrazovkách (2xl+) nefunguje toggle
		}
		const newSet = new Set(expandedCards);
		if (newSet.has(index)) {
			newSet.delete(index);
		} else {
			newSet.add(index);
		}
		expandedCards = newSet;
	}
</script>

<PageHero
	image={heroPhoto}
	mobileImage={heroPhotoMobile}
	alt={translate("heroAlt")}
	heading={translate("heroHeading")}
	text={translate("heroText")}
	objectPosition="center 5%"
/>

<section class="full-width bg-zms-grey grid-container">
	<div class="container-center flex flex-col gap-8 sm:gap-10 md:gap-12 lg:gap-16">
		<div class="flex flex-col gap-4 md:gap-10">
			<h2 class="text-center"><Formatted text={translate("pathHeading")} /></h2>
			<p class="font-normal flex justify-center text-center text-sm sm:text-base md:text-lg/[1.4] text-optical-black">
				<span class="lg:w-[75%]">
					<Formatted text={translate("pathText")} />
				</span>
			</p>
		</div>
		<ProcessWorkflow />
	</div>
</section>

<section class="container-center flex flex-col gap-8 sm:gap-12 md:gap-16 lg:gap-24">
	<h2 class="text-center"><Formatted text={translate("servicesHeading")} /></h2>
	<ServiceCardsGrid {categories} />
</section>

<section class="full-width grid-container bg-zms-grey py-6 sm:py-8 md:py-12">
	<div class="container-center grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-20">
		<StickySection>
			<div class="gap-8 lg:gap-10 flex flex-col">
				<h2
					class="text-zms-blue font-light text-left tracking-[-4%] text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl"
				>
					<Formatted text={translate("servicesMainHeading")} />
				</h2>
				<div class="flex flex-col gap-4">
					<p
						class="font-normal text-sm sm:text-base md:text-lg/[1.4] text-optical-black flex flex-col gap-3 sm:gap-4 md:gap-6 h-full"
					>
						<Formatted text={translate("servicesDescription")} />
					</p>
					<p
						class="font-normal text-sm sm:text-base md:text-lg/[1.4] text-optical-black flex flex-col gap-3 sm:gap-4 md:gap-6 h-full"
					>
						<Formatted text={translate("servicesContactText")} />
					</p>
				</div>
				<div>
					<Link href="#{contactId[tenant.locale]}" class="mt-6" variant={ButtonVariant.OutlinedWithIcon}>
						<TextWithIcon>
							{translate("servicesContactButton")}
						</TextWithIcon>
					</Link>
				</div>
			</div>
		</StickySection>
		<div class="flex flex-col gap-2">
			{#each Array.from({ length: 8 }) as _, index (index)}
				{#if index === 7}
					<Link
						href={getCataloguePath(tenant.locale)}
						class="group bg-white rounded-2xl flex gap-4 p-4 transition-all duration-300 ease-in-out 2xl:overflow-hidden hover:shadow-md"
						variant={ButtonVariant.Transparent}
					>
						<div class="flex-shrink-0" style="width: 96px; height: 96px; min-width: 96px;">
							<Media
								source={{ src: serviceImages[index] ?? "", type: "image/webp" }}
								alt=""
								class="rounded-[0.25rem] object-cover w-full h-full"
								width={96}
								height={96}
							/>
						</div>
						<div class="flex flex-col justify-center flex-1 min-h-[96px] relative">
							<h3
								class="font-medium text-[1.25rem]/[1.2] text-zms-dark-blue transition-all duration-300 ease-in-out {`service${index + 1}Description` in
								translations
									? '2xl:group-hover:transform 2xl:group-hover:-translate-y-8'
									: ''}"
							>
								{translate(`service${index + 1}Title` as keyof typeof translations)}
							</h3>
							{#if `service${index + 1}Description` in translations}
								<p
									class="text-xs/[1.3] sm:text-sm text-gray-600 2xl:opacity-0 transition-all duration-300 ease-in-out 2xl:group-hover:opacity-100 2xl:absolute 2xl:top-10 mt-2 2xl:mt-0"
								>
									{translate(`service${index + 1}Description` as keyof typeof translations)}
								</p>
							{/if}
						</div>
						<div class="justify-end items-center flex flex-shrink-0">
							<Icon icon={chevron} class="h-6 text-zms-blue" />
						</div>
					</Link>
				{:else}
					<button
						type="button"
						class="group bg-white rounded-2xl flex gap-4 p-4 transition-all duration-300 ease-in-out 2xl:overflow-hidden 2xl:cursor-default cursor-pointer text-left w-full 2xl:active:transform-none active:scale-[0.98]"
						onclick={(): void => {
							toggleCard(index);
						}}
					>
						<div class="flex-shrink-0" style="width: 96px; height: 96px; min-width: 96px;">
							<Media
								source={{ src: serviceImages[index] ?? "", type: "image/webp" }}
								alt=""
								class="rounded-[0.25rem] object-cover w-full h-full"
								width={96}
								height={96}
							/>
						</div>
						<div class="flex flex-col justify-center flex-1 min-h-[96px] relative">
							<h3
								class="font-medium text-[1.25rem]/[1.2] text-zms-dark-blue transition-all duration-300 ease-in-out {`service${index + 1}Description` in
								translations
									? '2xl:group-hover:transform 2xl:group-hover:-translate-y-8'
									: ''}"
							>
								{translate(`service${index + 1}Title` as keyof typeof translations)}
							</h3>
							{#if `service${index + 1}Description` in translations}
								<div
									class="overflow-hidden transition-all duration-300 ease-in-out 2xl:absolute 2xl:top-10 2xl:mt-0 {expandedCards.has(
										index,
									)
										? 'max-h-40 mt-2'
										: 'max-h-0 mt-0'} 2xl:max-h-none 2xl:opacity-0 2xl:group-hover:opacity-100"
								>
									<p class="text-xs/[1.3] sm:text-sm text-gray-600">
										{translate(`service${index + 1}Description` as keyof typeof translations)}
									</p>
								</div>
							{/if}
						</div>
						{#if `service${index + 1}Description` in translations}
							<div class="flex items-center 2xl:hidden flex-shrink-0">
								<svg
									class="w-5 h-5 text-zms-blue transition-transform duration-300 {expandedCards.has(index)
										? 'rotate-180'
										: ''}"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
								</svg>
							</div>
						{/if}
					</button>
				{/if}
			{/each}
		</div>
	</div>
</section>

<section class="container-center flex justify-center items-center flex-col gap-8 sm:gap-12 md:gap-10">
	<h2 class="sm:text-center max-md:text-lg text-zms-blue font-medium tracking-[-4%] px-4 sm:px-0">
		<Formatted text={translate("weldingHeading")} />
	</h2>
	<div class="flex flex-col items-start sm:items-center px-4 sm:px-0">
		<ul
			class="flex flex-col gap-2 sm:gap-3 text-left sm:text-center text-sm sm:text-base md:text-[18px]/[1.4] text-zms-optical-black w-full max-w-md sm:w-fit"
		>
			<li
				class="flex text-sm sm:text-base md:text-[18px]/[1.4] text-zms-optical-black items-start sm:justify-center sm:items-center gap-2 sm:gap-3"
			>
				<span class="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-zms-optical-black rounded-full flex-shrink-0 mt-1.5 sm:mt-0"></span>
				<span class="flex-1 sm:flex-none">{translate("weldingMethod1")}</span>
			</li>
			<li
				class="flex text-sm sm:text-base md:text-[18px]/[1.4] text-zms-optical-black items-start sm:justify-center sm:items-center gap-2 sm:gap-3"
			>
				<span class="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-zms-optical-black rounded-full flex-shrink-0 mt-1.5 sm:mt-0"></span>
				<span class="flex-1 sm:flex-none">{translate("weldingMethod2")}</span>
			</li>
			<li
				class="flex text-sm sm:text-base md:text-[18px]/[1.4] text-zms-optical-black items-start sm:justify-center sm:items-center gap-2 sm:gap-3"
			>
				<span class="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-zms-optical-black rounded-full flex-shrink-0 mt-1.5 sm:mt-0"></span>
				<span class="flex-1 sm:flex-none">{translate("weldingMethod3")}</span>
			</li>
			<li
				class="flex text-sm sm:text-base md:text-[18px]/[1.4] text-zms-optical-black items-start sm:justify-center sm:items-center gap-2 sm:gap-3"
			>
				<span class="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-zms-optical-black rounded-full flex-shrink-0 mt-1.5 sm:mt-0"></span>
				<span class="flex-1 sm:flex-none">{translate("weldingMethod4")}</span>
			</li>
			<li
				class="flex text-sm sm:text-base md:text-[18px]/[1.4] text-zms-optical-black items-start sm:justify-center sm:items-center gap-2 sm:gap-3"
			>
				<span class="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-zms-optical-black rounded-full flex-shrink-0 mt-1.5 sm:mt-0"></span>
				<span class="flex-1 sm:flex-none">{translate("weldingMethod5")}</span>
			</li>
		</ul>
		<Link href={getAboutPath(tenant)} variant={ButtonVariant.OutlinedWithIcon} class="mt-8 sm:mt-12 md:mt-16">
			<TextWithIcon>
				{translate("weldingButtonText")}
			</TextWithIcon>
		</Link>
	</div>
</section>

<WhyCooperateCarousel />

<ContactForm heading="withText" />
